/**
 * Gioco "Indovina l'animale".
 * Sistema di punteggio per similarità: ogni attributo corretto vale 1 punto.
 * Il colore della card del tentativo comunica la somiglianza con l'animale misterioso.
 */

import type { BoardCallbacks, GameModule, GameView, GuessResult } from '@core/types';
import { dailyKey, seededIndex } from '@core/daily';
import { t } from '@core/i18n';
import {
  attemptsUntilHint,
  autocompleteValues as buildAutocompleteValues,
  availableHints,
  findAnimal,
  nextAnimalHint,
  scoreVsTarget,
  type Animal,
  type AnimalHint,
  type AnimalsData,
  type AnimalsGuess,
  type AnimalsState,
} from './logic';

export type {
  Animal,
  AnimalHint,
  AnimalsData,
  AnimalsGuess,
  AnimalsState,
  Diet,
  Habitat,
  Klass,
  ScoreDetails,
  Size,
} from './logic';

let cache: AnimalsData | null = null;

async function loadAnimals(): Promise<AnimalsData> {
  if (cache) return cache;
  const res = await fetch(import.meta.env.BASE_URL + 'data/animals.json');
  if (!res.ok) throw new Error('Failed to load animals');
  cache = (await res.json()) as AnimalsData;
  return cache;
}

/**
 * Colore basato su score/max.
 * 0 = verde freddo scuro, ~0.5 = ambra, 1 = corallo caldo.
 */
function scoreColor(score: number, max: number): string {
  const ratio = max > 0 ? score / max : 0;
  let hue: number, sat: number, lig: number;
  if (ratio <= 0.5) {
    const t2 = ratio * 2;
    hue = Math.round(170 - t2 * 125);
    sat = Math.round(26 + t2 * 48);
    lig = Math.round(22 + t2 * 24);
  } else {
    const t2 = (ratio - 0.5) * 2;
    hue = Math.round(45 - t2 * 28);
    sat = Math.round(74 + t2 * 10);
    lig = Math.round(46 + t2 * 6);
  }
  return `hsl(${hue}, ${sat}%, ${lig}%)`;
}

function tAttr(ns: string, value: string): string {
  const key = `animals.${ns}.${value}`;
  const result = t(key);
  return result === key ? value : result;
}

function rangeKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z]+/g, '_').replace(/^_|_$/g, '');
}

function tRange(values: string[]): string {
  return values
    .map((value) => {
      const key = `animals.range.${rangeKey(value)}`;
      const result = t(key);
      return result === key ? value : result;
    })
    .join(', ');
}

function hintValue(target: Animal, hint: AnimalHint): string {
  if (hint === 'continents') return tRange(target.continents);
  if (hint === 'legs') return String(target.legs);
  if (hint === 'clue') return target.clue;
  return tAttr(hint, String(target[hint]));
}

function buildBoard(
  container: HTMLElement,
  state: AnimalsState,
  callbacks: BoardCallbacks<AnimalsGuess>,
): void {
  container.innerHTML = '';
  if (!cache) return;

  const animals = cache.animals;
  const target = animals.find((a) => a.id === state.targetId);
  if (!target) return;

  const wrap = document.createElement('div');
  wrap.className = 'grid gap-3 py-1';

  // ── Pulsante indizio ──────────────────────────────────────
  const hints = availableHints(target);
  const nextHint = nextAnimalHint(target, state.hintsRevealed, state.guesses.length);
  const lockedAttempts = attemptsUntilHint(state.guesses.length);
  const allRevealed = hints.every((attr) => state.hintsRevealed.includes(attr));

  if (callbacks.onRequestHint !== undefined) {
    const hintBtn = document.createElement('button');
    hintBtn.className = 'btn-ghost text-sm w-full';
    hintBtn.textContent = allRevealed
      ? t('animals.hint.none_left')
      : lockedAttempts > 0
        ? t(lockedAttempts === 1 ? 'animals.hint.locked_one' : 'animals.hint.locked', { n: lockedAttempts })
        : t('animals.hint.request');
    hintBtn.disabled = allRevealed || !nextHint;
    hintBtn.addEventListener('click', callbacks.onRequestHint);
    wrap.appendChild(hintBtn);
  }

  // ── Indizi rivelati ───────────────────────────────────────
  if (state.hintsRevealed.length > 0) {
    const hintsBox = document.createElement('div');
    hintsBox.className = 'flex flex-wrap gap-1';
    for (const attr of state.hintsRevealed) {
      const chip = document.createElement('div');
      chip.className = attr === 'clue' ? 'chip animals-clue-chip text-xs' : 'chip text-xs text-accent';
      const val = hintValue(target, attr);
      chip.textContent = t(`animals.hint.${attr}`, { value: val });
      hintsBox.appendChild(chip);
    }
    wrap.appendChild(hintsBox);
  }

  // ── Storia tentativi (punteggio più alto prima) ───────────
  const guessIds = [...state.guesses].sort((a, b) => {
    const sa = state.scoreByGuess[a]?.score ?? 0;
    const sb = state.scoreByGuess[b]?.score ?? 0;
    return sb - sa;
  });

  if (guessIds.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'text-slate-400 text-sm text-center py-4';
    empty.textContent = '🦁❓';
    wrap.appendChild(empty);
  }

  for (const gid of guessIds) {
    const animal = animals.find((a) => a.id === gid);
    if (!animal) continue;
    const { score, max, details } = state.scoreByGuess[gid] ?? {
      score: 0,
      max: 6,
      details: { klass: false, habitat: false, diet: false, size: false, continents: false, legs: false },
    };
    const bgColor = scoreColor(score, max);

    const card = document.createElement('div');
    card.className = 'rounded-xl p-3 grid gap-1';
    card.style.backgroundColor = bgColor;
    card.style.boxShadow = `0 2px 12px ${bgColor}55`;

    const nameRow = document.createElement('div');
    nameRow.className = 'flex items-center justify-between';
    const nameEl = document.createElement('span');
    nameEl.className = 'font-bold text-white drop-shadow-sm';
    nameEl.textContent = animal.name;
    const scoreEl = document.createElement('span');
    scoreEl.className = 'text-xs font-mono text-white/70 tabular-nums';
    scoreEl.textContent = t('animals.score', { score: String(score), max: String(max) });
    nameRow.append(nameEl, scoreEl);

    const attrsRow = document.createElement('div');
    attrsRow.className = 'flex flex-wrap gap-x-3 gap-y-0.5 text-xs';
    const attrEntries: Array<[string, boolean | undefined, string]> = [
      ['klass', details.klass, tAttr('klass', animal.klass)],
      ['habitat', details.habitat, tAttr('habitat', animal.habitat)],
      ['diet', details.diet, tAttr('diet', animal.diet)],
      ['size', details.size, tAttr('size', animal.size)],
      ['continents', details.continents, tRange(animal.continents)],
      ['legs', details.legs, t('animals.legs_value', { value: animal.legs })],
    ];
    for (const [, match, label] of attrEntries) {
      const chip = document.createElement('span');
      chip.className = match ? 'text-white/90 font-medium' : 'text-white/40 line-through';
      chip.textContent = (match ? '✓ ' : '✗ ') + label;
      attrsRow.appendChild(chip);
    }

    card.append(nameRow, attrsRow);
    wrap.appendChild(card);
  }

  container.appendChild(wrap);
}

export const animalsGame: GameModule<AnimalsState, AnimalsGuess> = {
  id: 'animals',
  metadata: {
    name: t('animals.name'),
    description: t('animals.description'),
    icon: '🦁',
    iconUrl: '/images/loghi/animals.png',
    minPlayers: 1,
    maxPlayers: 8,
    supportsTeams: true,
    supportsDaily: true,
    rendersOwnHistory: true,
  },

  async loadAssets() {
    await loadAnimals();
  },

  initState(config) {
    if (!cache) throw new Error('Assets not loaded');
    const list = cache.animals;
    const targetId = config.daily
      ? list[seededIndex(`animals::${dailyKey()}`, list.length)].id
      : list[Math.floor(Math.random() * list.length)].id;
    return {
      targetId,
      guesses: [],
      scoreByGuess: {},
      hintsRevealed: [],
      daily: config.daily,
      surrendered: false,
    };
  },

  validateGuess(_state, raw): AnimalsGuess | null {
    if (!cache) return null;
    let candidate = raw;
    try {
      const p = JSON.parse(raw) as { id?: string; name?: string };
      if (p?.id) {
        const a = cache.animals.find((aa) => aa.id === p.id);
        if (a) return { id: a.id, name: a.name };
      }
      if (p?.name) candidate = p.name;
    } catch {
      /* not JSON */
    }
    const a = findAnimal(candidate, cache.animals);
    return a ? { id: a.id, name: a.name } : null;
  },

  isDuplicateGuess(state, guess): boolean {
    return state.guesses.includes(guess.id);
  },

  applyGuess(state, guess) {
    if (!cache) throw new Error('Assets not loaded');
    const guessed = cache.animals.find((a) => a.id === guess.id)!;
    const target = cache.animals.find((a) => a.id === state.targetId)!;
    const correct = guessed.id === target.id;
    const { score, max, details } = scoreVsTarget(guessed, target);
    const color = correct ? 'hsl(148, 53%, 38%)' : scoreColor(score, max);

    const result: GuessResult = {
      correct,
      feedback: correct
        ? '✓ ' + t('win.title')
        : t('animals.score', { score: String(score), max: String(max) }),
      score,
      meta: { color, score, max, details },
    };

    const next: AnimalsState = {
      ...state,
      guesses: state.guesses.includes(guess.id) ? state.guesses : [...state.guesses, guess.id],
      scoreByGuess: {
        ...state.scoreByGuess,
        [guess.id]: { score, max, details },
      },
    };
    return { state: next, result };
  },

  isWon(state) {
    return state.guesses.includes(state.targetId);
  },

  surrender(state) {
    return { ...state, surrendered: true };
  },

  isGameOver(state) {
    return state.surrendered === true || state.guesses.includes(state.targetId);
  },

  revealHint(state) {
    if (!cache) return state;
    const target = cache.animals.find((a) => a.id === state.targetId);
    if (!target) return state;
    const next = nextAnimalHint(target, state.hintsRevealed, state.guesses.length);
    if (!next) return state;
    return { ...state, hintsRevealed: [...state.hintsRevealed, next] };
  },

  getTargetLabel(state) {
    if (!cache) return null;
    return cache.animals.find((a) => a.id === state.targetId)?.name ?? null;
  },

  autocompleteValues() {
    if (!cache) return [];
    return buildAutocompleteValues(cache.animals);
  },

  renderBoard(container, state, callbacks): GameView {
    buildBoard(container, state, callbacks);
    return {
      update(newState: unknown) {
        buildBoard(container, newState as AnimalsState, callbacks);
      },
      destroy() {
        container.innerHTML = '';
      },
    };
  },

  serializeState(state) { return state; },
  deserializeState(raw) { return raw as AnimalsState; },
};

